import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowOutward,
  EmojiEventsOutlined,
  LinkOutlined,
  SportsEsportsOutlined,
  TuneOutlined,
} from '@mui/icons-material';
import { FaDiscord } from 'react-icons/fa';
import V2PageShell from 'features/v2/V2PageShell';
import { v2Tokens } from 'theme/v2Theme';

const discordInstallUrl =
  'https://discord.com/oauth2/authorize?client_id=1107287420673720440&scope=bot&permissions=138513139776';

const workflowSteps = [
  {
    label: '01 / Start',
    title: 'Create an event with /play',
    description:
      'The Discord app starts a community event with a selected production game, duration, and Ordinal collection.',
    image: '/assets/discord/demo1_1.png',
    imageAlt: 'Discord showing an Ordinals Play event created with the play command',
    icon: SportsEsportsOutlined,
  },
  {
    label: '02 / Configure',
    title: 'Choose game and collection',
    description:
      'Discord command options guide the host through the game and eligible collection selection.',
    image: '/assets/discord/demo3.png',
    imageAlt: 'Discord play command collection selection options',
    icon: TuneOutlined,
  },
  {
    label: '03 / Launch',
    title: 'Share the generated game link',
    description:
      'The event message provides participants with the existing Ordinals Play game destination.',
    image: '/assets/discord/demo4.png',
    imageAlt: 'Ordinals Play Discord event message with a Go to Game link',
    icon: LinkOutlined,
  },
  {
    label: '04 / Results',
    title: 'Review the competition result',
    description:
      'After the event, the Discord app presents the winning score and the recorded top scorers.',
    image: '/assets/discord/demo6.png',
    imageAlt: 'Ordinals Play Discord event winner and top scorers',
    icon: EmojiEventsOutlined,
  },
];

const DiscordBotPage = () => (
  <V2PageShell>
    <Container
      component="main"
      maxWidth={false}
      sx={{ width: '100%', maxWidth: 1328, px: { xs: 2, sm: 3 }, py: { xs: 3, md: 6 } }}
    >
      <Box
        component="header"
        sx={{
          pb: { xs: 3, md: 4 },
          mb: { xs: 3, md: 4 },
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Typography variant="overline" color="primary.main">
          Discord Integration // Production
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 1,
            maxWidth: 900,
            color: '#fff',
            fontSize: { xs: 38, sm: 54, md: 70 },
            fontWeight: 900,
            fontStyle: 'italic',
            lineHeight: 0.95,
            letterSpacing: '-0.055em',
            textTransform: 'uppercase',
          }}
        >
          Host the arcade from Discord
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ mt: 2, maxWidth: 720, fontSize: { xs: 13, md: 15 }, lineHeight: 1.75 }}
        >
          Add the Ordinals Play Discord app to a server, configure a game event,
          share the generated game link, and review the recorded competition result.
        </Typography>
      </Box>

      <Card
        component="section"
        aria-labelledby="discord-install-title"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: { xs: 2.5, md: 4 },
          borderRadius: v2Tokens.radius.panel,
          background:
            'radial-gradient(circle at 8% 20%, rgba(247,147,26,0.12), transparent 38%), radial-gradient(circle at 92% 76%, rgba(88,101,242,0.16), transparent 42%), #171A21',
          boxShadow: '0 24px 56px rgba(0,0,0,0.24)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(320px, 0.9fr)' },
            alignItems: 'center',
            gap: { xs: 3, md: 5 },
          }}
        >
          <Stack alignItems="flex-start" spacing={2.25}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 52,
                height: 52,
                color: '#fff',
                border: '1px solid rgba(88,101,242,0.34)',
                borderRadius: v2Tokens.radius.nested,
                backgroundColor: 'rgba(88,101,242,0.16)',
              }}
            >
              <FaDiscord aria-hidden="true" size={25} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: '#8B95FF' }}>
                Discord Server App
              </Typography>
              <Typography id="discord-install-title" component="h2" variant="h2" sx={{ mt: 0.75 }}>
                Ordinals Play Discord App
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1.25, maxWidth: 570, fontSize: 13, lineHeight: 1.7 }}>
                The app supports the existing community-event flow shown in the bundled
                Ordinals Play Discord captures. Installation opens Discord&apos;s own authorization screen.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                component="a"
                href={discordInstallUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<FaDiscord />}
                endIcon={<ArrowOutward />}
                aria-label="Add Ordinals Play to Discord (opens in a new tab)"
                sx={{ minHeight: 50, px: 3, width: { xs: '100%', sm: 'auto' } }}
              >
                Add to Discord
              </Button>
              <Chip label="Manage Server permission required" variant="outlined" sx={{ minHeight: 40 }} />
            </Stack>
          </Stack>

          <Box
            sx={{
              position: 'relative',
              p: 1.25,
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: v2Tokens.radius.nested,
              backgroundColor: 'rgba(7,9,13,0.72)',
              boxShadow: 'inset 0 0 30px rgba(0,0,0,0.30)',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                opacity: 0.24,
                background:
                  'repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)',
              },
            }}
          >
            <Box
              component="img"
              src="/assets/discord/demo1_1.png"
              alt="Ordinals Play Discord event preview"
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                display: 'block',
                borderRadius: v2Tokens.radius.control,
              }}
            />
          </Box>
        </Box>
      </Card>

      <Box component="section" aria-labelledby="discord-workflow-title" sx={{ mt: { xs: 4, md: 5 } }}>
        <Typography variant="overline" color="primary.main">
          Existing Event Flow
        </Typography>
        <Typography id="discord-workflow-title" component="h2" variant="h2" sx={{ mt: 0.75, mb: 2.5 }}>
          From command to competition result
        </Typography>

        <Grid container spacing={2.5}>
          {workflowSteps.map(({ label, title, description, image, imageAlt, icon: Icon }) => (
            <Grid item xs={12} sm={6} key={label}>
              <Card
                sx={{
                  height: '100%',
                  p: 2.5,
                  borderRadius: v2Tokens.radius.panel,
                  backgroundColor: '#171A21',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Typography variant="overline" color="primary.main">
                    {label}
                  </Typography>
                  <Icon aria-hidden="true" sx={{ color: '#737373', fontSize: 20 }} />
                </Stack>
                <Typography component="h3" variant="h3" sx={{ mt: 1 }}>
                  {title}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1, minHeight: { sm: 62 }, fontSize: 12, lineHeight: 1.65 }}>
                  {description}
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    p: 1,
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: v2Tokens.radius.nested,
                    backgroundColor: v2Tokens.surfaceInset,
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt={imageAlt}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: { xs: 210, md: 250 },
                      display: 'block',
                      objectFit: 'contain',
                      borderRadius: v2Tokens.radius.control,
                    }}
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Card
        component="section"
        aria-labelledby="discord-requirements-title"
        sx={{
          mt: { xs: 4, md: 5 },
          p: { xs: 2.5, md: 3 },
          borderRadius: v2Tokens.radius.panel,
          backgroundColor: 'rgba(7,9,13,0.64)',
        }}
      >
        <Typography variant="overline" color="primary.main">
          Setup Notes
        </Typography>
        <Typography id="discord-requirements-title" component="h2" variant="h3" sx={{ mt: 0.75 }}>
          Discord server access
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 820, fontSize: 12, lineHeight: 1.7 }}>
          Discord requires the installer to have Manage Server permission. This is an
          OAuth bot installation, not a downloadable package. The only command claimed
          here is <Box component="code" sx={{ color: '#E5E5E5', fontFamily: '"JetBrains Mono", monospace' }}>/play</Box>,
          which is visible in the bundled Ordinals Play captures.
        </Typography>
      </Card>
    </Container>
  </V2PageShell>
);

export default DiscordBotPage;
